import {useEffect, useState} from "react";
import {useForm, Controller} from "react-hook-form";
import {App as AntdApp, Table, Tag, Space, Button as AntdButton, Popconfirm, Row, Col, Divider} from "antd";
import {PageHeader, Loading, FontAwesomeIcon, Button, ModalForm} from "~/components";
import {InputField, NumberField, SelectField, TextAreaField, CheckBoxField} from "~/components/Forms";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {
	useGetAdminPlansQuery, useCreatePlanMutation, useUpdatePlanMutation, useDeletePlanMutation,
} from "~/reduxs/api/planApiSlice";
import {nf, planColor} from "../util";
import style from "../style/Member.module.scss";
import className from "classnames/bind";

const cn = className.bind(style);

const INT_FIELDS = [
	{key: 'ai_token_limit', label: 'AI Token / tháng'},
	{key: 'url_checks_per_period', label: 'URL kiểm tra / tuần'},
	{key: 'max_projects', label: 'Số dự án'},
	{key: 'max_urls_per_project', label: 'URL tối đa / dự án'},
	{key: 'crawl_min_interval_min', label: 'Tần suất crawl (phút)'},
	{key: 'crawl_priority', label: 'Ưu tiên crawl (1-4)'},
	{key: 'history_retention_days', label: 'Lưu lịch sử (ngày, 0=∞)'},
	{key: 'gsc_accounts', label: 'Số tài khoản GSC'},
];

const BOOL_FIELDS = [
	{key: 'can_connect_gsc', label: 'Kết nối GSC'},
	{key: 'can_ai_content', label: 'AI sinh nội dung'},
	{key: 'can_ai_deep', label: 'Trợ lý AI chuyên sâu'},
	{key: 'can_export_pdf', label: 'Xuất PDF'},
	{key: 'can_export_excel', label: 'Xuất Excel'},
	{key: 'can_keyword_tracking', label: 'Theo dõi từ khoá'},
	{key: 'can_use_api', label: 'API hệ thống'},
	{key: 'can_email_notification', label: 'Thông báo Email'},
	{key: 'require_own_ai_key', label: 'Bắt buộc khoá AI riêng'},
];

const EMPTY_LIMITS = {
	ai_token_limit: 0, url_checks_per_period: 0, max_projects: 0, max_urls_per_project: 0,
	crawl_min_interval_min: 0, crawl_priority: 1, history_retention_days: 0, gsc_accounts: 0,
	can_connect_gsc: false, can_ai_content: false, can_ai_deep: false, can_export_pdf: false,
	can_export_excel: false, can_keyword_tracking: false, can_use_api: false, can_email_notification: false,
	require_own_ai_key: false,
};

const EMPTY_VALUES = {
	slug: '', name: '', description: '', price: 0, billing_cycle: 'month', status: 'active', order: 0,
	limits: {...EMPTY_LIMITS},
};

function PlanManager() {
	const {notification} = AntdApp.useApp();
	const {data: plans = [], isLoading} = useGetAdminPlansQuery();
	const [createPlan, {isLoading: creating}] = useCreatePlanMutation();
	const [updatePlan, {isLoading: updating}] = useUpdatePlanMutation();
	const [deletePlan] = useDeletePlanMutation();

	const [open, setOpen] = useState(false);
	const [editing, setEditing] = useState(null); // null = tạo mới; object = sửa

	const {control, handleSubmit, reset} = useForm({defaultValues: EMPTY_VALUES});

	// Nạp lại giá trị mỗi khi mở (tạo mới → mặc định; sửa → giá trị gói).
	useEffect(() => {
		if (!open) return;
		if (editing) {
			reset({
				slug: editing.slug, name: editing.name, description: editing.description || '',
				price: editing.price || 0, billing_cycle: editing.billing_cycle || 'month',
				status: editing.status || 'active', order: editing.order || 0,
				limits: {...EMPTY_LIMITS, ...(editing.limits || {})},
			});
		} else {
			reset(EMPTY_VALUES);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, editing]);

	const openCreate = () => { setEditing(null); setOpen(true); };
	const openEdit = (plan) => { setEditing(plan); setOpen(true); };

	const submit = async (values) => {
		const body = {
			name: values.name, description: values.description || '', price: values.price || 0,
			billing_cycle: values.billing_cycle, status: values.status, order: values.order || 0,
			limits: values.limits || {},
		};
		try {
			if (editing) {
				await updatePlan({id: editing.id, ...body}).unwrap();
				notification.success({message: 'Đã cập nhật gói'});
			} else {
				await createPlan({slug: values.slug, ...body}).unwrap();
				notification.success({message: 'Đã tạo gói'});
			}
			setOpen(false);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e)});
		}
	};

	const remove = async (plan) => {
		try {
			await deletePlan(plan.id).unwrap();
			notification.success({message: 'Đã xóa gói'});
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e)});
		}
	};

	const columns = [
		{
			title: 'Gói', key: 'plan',
			render: (_, p) => (
				<div className={cn('member-cell')}>
					<div className={cn('name')}><Tag color={planColor(p.slug)}>{p.name}</Tag></div>
					<div className={cn('sub')}>{p.slug}</div>
				</div>
			),
		},
		{title: 'Giá', key: 'price', render: (_, p) => (p.price > 0 ? `${nf(p.price)}đ` : 'Miễn phí')},
		{
			title: 'Trạng thái', key: 'status',
			render: (_, p) => <Tag color={p.status === 'active' ? 'green' : 'default'}>{p.status === 'active' ? 'Đang bán' : 'Ẩn'}</Tag>,
		},
		{title: 'AI Token/tháng', key: 'ai', align: 'right', render: (_, p) => nf(p.limits?.ai_token_limit)},
		{title: 'Dự án', key: 'proj', align: 'right', render: (_, p) => nf(p.limits?.max_projects)},
		{title: 'URL/tuần', key: 'url', align: 'right', render: (_, p) => nf(p.limits?.url_checks_per_period)},
		{title: 'Thành viên', dataIndex: 'subscribers', key: 'subscribers', align: 'right', render: (v) => nf(v)},
		{
			title: '', key: 'action', align: 'right',
			render: (_, p) => (
				<Space>
					<AntdButton size="small" onClick={() => openEdit(p)}>Sửa</AntdButton>
					<Popconfirm title={`Xóa gói "${p.name}"?`} onConfirm={() => remove(p)} okText="Xóa" cancelText="Thôi">
						<AntdButton size="small" danger disabled={p.slug === 'free'}>Xóa</AntdButton>
					</Popconfirm>
				</Space>
			),
		},
	];

	return (
		<div className="container">
			<PageHeader
				icon="fa-light fa-layer-group"
				title="Quản lý gói"
				subtitle="Cấu hình các gói dịch vụ, giá và hạn mức. Thay đổi có hiệu lực ngay."
				actions={
					<Button white onClick={openCreate} leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}>
						Thêm gói
					</Button>
				}
			/>

			{isLoading ? <Loading /> : (
				<Table columns={columns} dataSource={plans} rowKey="id" pagination={false} scroll={{x: 'max-content'}} />
			)}

			<ModalForm
				open={open}
				icon="fa-light fa-layer-group"
				title={editing ? `Sửa gói: ${editing.name}` : 'Thêm gói mới'}
				subtitle="Cấu hình cột lõi + hạn mức + tính năng của gói."
				onCancel={() => setOpen(false)}
				onOk={handleSubmit(submit)}
				okText={editing ? 'Lưu' : 'Tạo'}
				loading={creating || updating}
				width={780}
			>
				<Row gutter={16}>
					<Col span={8}>
						<Controller control={control} name="slug" render={({field}) => (
							<InputField label="Slug (mã gói)" placeholder="vd: pro" disabled={!!editing} {...field} />
						)} />
					</Col>
					<Col span={8}>
						<Controller control={control} name="name" render={({field}) => (
							<InputField label="Tên gói" placeholder="vd: Chuyên nghiệp" {...field} />
						)} />
					</Col>
					<Col span={8}>
						<Controller control={control} name="price" render={({field}) => (
							<NumberField label="Giá (VND)" min={0} step={1000} {...field} />
						)} />
					</Col>
					<Col span={8}>
						<Controller control={control} name="billing_cycle" render={({field}) => (
							<SelectField label="Chu kỳ" options={[{value: 'month', label: 'Hàng tháng'}, {value: 'year', label: 'Hàng năm'}]} {...field} />
						)} />
					</Col>
					<Col span={8}>
						<Controller control={control} name="status" render={({field}) => (
							<SelectField label="Trạng thái" options={[{value: 'active', label: 'Đang bán'}, {value: 'inactive', label: 'Ẩn'}]} {...field} />
						)} />
					</Col>
					<Col span={8}>
						<Controller control={control} name="order" render={({field}) => (
							<NumberField label="Thứ tự" min={0} {...field} />
						)} />
					</Col>
					<Col span={24}>
						<Controller control={control} name="description" render={({field}) => (
							<TextAreaField label="Mô tả" rows={2} maxLength={500} {...field} />
						)} />
					</Col>
				</Row>

				<Divider orientation="left">Hạn mức</Divider>
				<Row gutter={16}>
					{INT_FIELDS.map((f) => (
						<Col span={8} key={f.key}>
							<Controller control={control} name={`limits.${f.key}`} render={({field}) => (
								<NumberField label={f.label} min={0} {...field} />
							)} />
						</Col>
					))}
				</Row>

				<Divider orientation="left">Tính năng</Divider>
				<Row gutter={16}>
					{BOOL_FIELDS.map((f) => (
						<Col span={8} key={f.key}>
							<Controller control={control} name={`limits.${f.key}`} render={({field}) => (
								<CheckBoxField value={!!field.value} onChange={(e) => field.onChange(e.target.checked)}>
									{f.label}
								</CheckBoxField>
							)} />
						</Col>
					))}
				</Row>
			</ModalForm>
		</div>
	);
}

export default PlanManager;
